import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import SideBarContainer from 'Containers/SideBarContainer';
import Title from '../../Components/Title';
import useEmployeePage from './Components/useEmployeePage';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Switch } from '@mui/material';
import { Controller } from 'react-hook-form';
import SearchTextField from 'Components/SeachTextField/SearchTextField';
import SelectField from 'Components/SelectField';
import useTerminalsApi from 'Hooks/useTerminalsApi';
import useDepartmentsApi from 'Hooks/useDepartmentsApi';
import { DataGrid, esES } from '@mui/x-data-grid';
import useTranslation from '../../Hooks/useTranslation';
import { MoreVert, Visibility } from '@mui/icons-material';
import { colors } from 'Constants/Colors';
import ConfirmDialog from 'Components/ConfirmDialog';
import useSwitch from 'Hooks/useSwitch';

const statusOptions = [
  { id: 'active', name: 'Activo' },
  { id: 'inactive', name: 'Inactivo' },
];

export default function Employee() {
  const { t } = useTranslation();
  const history = useHistory();
  const { control, rows, loading, pagination, page, pageSize, setPage, setPageSize, toggleStatus } = useEmployeePage();
  const { data: terminals } = useTerminalsApi({ init: true });
  const { data: departments } = useDepartmentsApi({ init: true });

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const { active: confirmOpen, activate: openConfirm, deactivate: closeConfirm } = useSwitch();
  const [pendingToggle, setPendingToggle] = useState(null);

  const handleToggleClick = (userId, currentStatus) => {
    setPendingToggle({ userId, currentStatus });
    openConfirm();
  };

  const handleConfirmToggle = () => {
    if (pendingToggle) {
      toggleStatus(pendingToggle.userId, pendingToggle.currentStatus);
    }
    setPendingToggle(null);
    closeConfirm();
  };

  const handleCancelToggle = () => {
    setPendingToggle(null);
    closeConfirm();
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleViewDetails = () => {
    if (!selectedRow) return;
    const user = [
      selectedRow.payrollNum, // [0] payrollNum
      selectedRow.fullName, // [1] name
      selectedRow.Department?.name, // [2] department
      selectedRow.JobTitle?.name, // [3] jobTitle
      selectedRow.role?.name, // [4] role
      selectedRow.Terminal?.name, // [5] terminal
      null, // [6] unused
      selectedRow.entryDate, // [7] entryDate
      selectedRow.status, // [8] status
      selectedRow.id, // [9] id
      null, // [10] padding
    ];
    history.push({
      pathname: '/empleado/desarrollo',
      state: { user },
    });
    handleMenuClose();
  };

  const columns = [
    { field: 'payrollNum', headerName: t('Employee.EmployeeNumber'), flex: 1 },
    { field: 'fullName', headerName: t('Employee.Name'), flex: 1.5 },
    {
      field: 'Department',
      headerName: t('Employee.Department'),
      flex: 1,
      valueGetter: params => params.row.Department?.name || '',
    },
    {
      field: 'JobTitle',
      headerName: t('Employee.JobTitle'),
      flex: 1,
      valueGetter: params => params.row.JobTitle?.name || '',
    },
    {
      field: 'role',
      headerName: t('Employee.Role'),
      flex: 1,
      valueGetter: params => params.row.role?.name || '',
    },
    {
      field: 'Terminal',
      headerName: t('Employee.Terminal'),
      flex: 1,
      valueGetter: params => params.row.Terminal?.name || '',
    },
    {
      field: 'status',
      headerName: t('Employee.Status'),
      flex: 0.7,
      renderCell: params => (
        <Switch
          checked={params.row.status}
          onChange={() => handleToggleClick(params.row.id, params.row.status)}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: colors.GREEN,
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: colors.GREEN,
            },
            '& .MuiSwitch-switchBase:not(.Mui-checked)': {
              color: colors.RED,
            },
            '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track': {
              backgroundColor: colors.RED,
            },
          }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: params => (
        <IconButton size="small" onClick={event => handleMenuOpen(event, params.row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <SideBarContainer>
      <Box display="flex" flexDirection="column" height="calc(100vh - 80px)" bgcolor="#f6f7fc" overflow="hidden">
        <Title title={t('Employee.Title')} subtitle={t('Employee.SubTitle')} />
        <Box
          display="flex"
          flexDirection="row"
          width="95%"
          margin="auto"
          justifyContent="space-between"
          alignItems="end"
        >
          <Controller
            name="search"
            control={control}
            render={({ field }) => <SearchTextField {...field} placeholder="Buscar" />}
          />
          <Box display="flex" flexDirection="row" gap={2}>
            <Controller
              name="terminal"
              control={control}
              render={({ field }) => <SelectField {...field} options={terminals} label={t('Employee.Terminal')} />}
            />
            <Controller
              name="department"
              control={control}
              render={({ field }) => <SelectField {...field} options={departments} label={t('Employee.Department')} />}
            />
            <Controller
              name="status"
              control={control}
              render={({ field }) => <SelectField {...field} options={statusOptions} label={t('Employee.Status')} />}
            />
          </Box>
        </Box>

        <Paper sx={{ width: '95%', margin: 'auto', mt: 2, flex: 1, minHeight: 0, mb: 2 }} elevation={0}>
          <DataGrid
            density="compact"
            rows={rows}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={pagination?.totalItems || 0}
            page={page}
            pageSize={pageSize}
            onPageChange={newPage => setPage(newPage)}
            onPageSizeChange={newSize => setPageSize(newSize)}
            rowsPerPageOptions={[15, 25, 50]}
            disableSelectionOnClick
            disableColumnMenu
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            sx={{
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
            }}
          />
        </Paper>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleViewDetails}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ver detalles</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
      <ConfirmDialog
        open={confirmOpen}
        title="Confirmar cambio de estatus"
        description={
          pendingToggle?.currentStatus
            ? '¿Estás seguro de que deseas inactivar a este empleado?'
            : '¿Estás seguro de que deseas activar a este empleado?'
        }
        onConfirm={handleConfirmToggle}
        onCancel={handleCancelToggle}
      />
    </SideBarContainer>
  );
}
